import { useCallback, useMemo } from 'react';
import type { Theme as ExtendedTheme } from '@mui/material/styles';
import {
    getPrintDividerColor,
    getPrintTextPrimaryColor,
} from '../../../../utils/print-theme.utils';

// Froala and the PDF exporter consume serialized color strings outside MUI's
// rendered CSS, so this module intentionally uses concrete palette values.

type HighlightPaletteEntry = {
    subtle?: string;
    light?: string;
    main?: string;
    strong?: string;
    dark?: string;
    solidBg?: string;
};

const buildHighlightStyle = (textColor: string, backgroundColor: string) =>
    `color: ${textColor}; background-color: ${backgroundColor};`;

const pickHighlightBackground = (
    paletteEntry: HighlightPaletteEntry | undefined,
    isDarkMode: boolean,
    fallback: string
) => {
    if (!paletteEntry) {
        return fallback;
    }

    if (isDarkMode) {
        return (
            paletteEntry.strong ||
            paletteEntry.dark ||
            paletteEntry.solidBg ||
            paletteEntry.main ||
            fallback
        );
    }

    return (
        paletteEntry.solidBg ||
        paletteEntry.main ||
        paletteEntry.subtle ||
        paletteEntry.light ||
        fallback
    );
};

const getHighlightTextColor = (
    theme: ExtendedTheme,
    backgroundColor: string,
    isDarkMode: boolean
) => {
    if (typeof theme.palette.getContrastText === 'function') {
        return theme.palette.getContrastText(backgroundColor);
    }

    return isDarkMode
        ? theme.palette.text.contrast || '#ffffff'
        : theme.palette.text.primary || '#17172b';
};

const buildPdfExportElement = (editor: any, theme: ExtendedTheme) => {
    const html = editor?.html?.get?.();

    if (typeof html !== 'string') {
        return undefined;
    }

    const exportElement = document.createElement('div');
    exportElement.className = 'fr-view';
    exportElement.style.color = getPrintTextPrimaryColor(theme);
    exportElement.innerHTML = html;

    exportElement
        .querySelectorAll<HTMLElement>('table, th, td, th *, td *')
        .forEach((element) => {
            element.style.setProperty(
                'color',
                getPrintTextPrimaryColor(theme),
                'important'
            );
            element.style.setProperty(
                'border-color',
                getPrintDividerColor(theme),
                'important'
            );
        });

    return exportElement;
};

export const useEditorPdfLightTheme = (theme: ExtendedTheme) => {
    const isDarkMode = theme.palette.mode === 'dark';

    const warningBackground = pickHighlightBackground(
        theme.palette.warning,
        isDarkMode,
        isDarkMode ? '#6b4b00' : '#fcf8f5'
    );
    const redBackground = pickHighlightBackground(
        theme.palette.error,
        isDarkMode,
        isDarkMode ? '#7a1a21' : '#fdf6f6'
    );
    const purpleBackground = pickHighlightBackground(
        theme.palette.primary,
        isDarkMode,
        isDarkMode ? '#4e2a73' : '#faf7fb'
    );
    const blueBackground = pickHighlightBackground(
        theme.palette.info,
        isDarkMode,
        isDarkMode ? '#0f3d7a' : '#f5f8ff'
    );
    const greenBackground = pickHighlightBackground(
        theme.palette.success,
        isDarkMode,
        isDarkMode ? '#1f5a36' : '#f6faf7'
    );
    const neutralBackground = isDarkMode
        ? theme.palette.action.active || '#2b2b3d'
        : theme.palette.action.selected || '#ececf4';

    const warningTextColor = getHighlightTextColor(
        theme,
        warningBackground,
        isDarkMode
    );
    const redTextColor = getHighlightTextColor(
        theme,
        redBackground,
        isDarkMode
    );
    const purpleTextColor = getHighlightTextColor(
        theme,
        purpleBackground,
        isDarkMode
    );
    const blueTextColor = getHighlightTextColor(
        theme,
        blueBackground,
        isDarkMode
    );
    const greenTextColor = getHighlightTextColor(
        theme,
        greenBackground,
        isDarkMode
    );
    const neutralTextColor = getHighlightTextColor(
        theme,
        neutralBackground,
        isDarkMode
    );

    const colorsText = useMemo(
        () => [
            theme.palette.text.primary,
            theme.palette.text.secondary,
            theme.palette.info.light,
            theme.palette.success.light,
            theme.palette.warning.light,
            theme.palette.error.light,
            theme.palette.primary.light,
            theme.palette.background.paper,
            theme.palette.divider,
            theme.palette.info.main,
            theme.palette.success.main,
            theme.palette.warning.main,
            theme.palette.error.main,
            theme.palette.primary.main,
            theme.palette.background.default,
            theme.palette.action.selected,
            theme.palette.info.dark,
            theme.palette.success.dark,
            theme.palette.warning.dark,
            theme.palette.error.dark,
            theme.palette.primary.dark,
        ],
        [theme]
    );

    const inlineStyles = useMemo(
        () => ({
            'Text highlight - Yellow': buildHighlightStyle(
                warningTextColor,
                warningBackground
            ),
            'Text highlight - Orange': buildHighlightStyle(
                warningTextColor,
                warningBackground
            ),
            'Text highlight - Red': buildHighlightStyle(
                redTextColor,
                redBackground
            ),
            'Text highlight - Purple': buildHighlightStyle(
                purpleTextColor,
                purpleBackground
            ),
            'Text highlight - Blue': buildHighlightStyle(
                blueTextColor,
                blueBackground
            ),
            'Text highlight - Neutral': buildHighlightStyle(
                neutralTextColor,
                neutralBackground
            ),
            'Text highlight - Green': buildHighlightStyle(
                greenTextColor,
                greenBackground
            ),
        }),
        [
            blueBackground,
            blueTextColor,
            greenBackground,
            greenTextColor,
            neutralBackground,
            neutralTextColor,
            purpleBackground,
            purpleTextColor,
            redBackground,
            redTextColor,
            warningBackground,
            warningTextColor,
        ]
    );

    const exportEditorToPdf = useCallback(
        (editor: any) => {
            const html2pdf = (window as any).html2pdf;

            if (!html2pdf) {
                return;
            }

            const exportElement = buildPdfExportElement(editor, theme);

            if (!exportElement) {
                return;
            }

            const exportHost = document.createElement('div');
            exportHost.appendChild(exportElement);
            document.body.appendChild(exportHost);

            const cleanup = () => {
                exportHost.remove();
            };

            const worker = html2pdf()
                .set({
                    margin: [10, 20],
                    html2canvas: {
                        useCORS: true,
                    },
                })
                .from(exportElement)
                .save();

            if (worker?.then) {
                worker.then(cleanup).catch(cleanup);
                return;
            }

            cleanup();
        },
        [theme]
    );

    const normalizeEditorHtml = useCallback(
        (value: string) => value.replace(/rgb\(204, 204, 204\)/gi, 'inherit'),
        []
    );

    return {
        colorsText,
        inlineStyles,
        exportEditorToPdf,
        normalizeEditorHtml,
    };
};
